/**
 * 统一请求实例（axios）。
 * - baseURL 留空，走 vite proxy `/api`（见 vite.config.ts）。
 * - 请求拦截器注入 Authorization: Bearer <token>（localStorage['bluplanet_token']）。
 * - 响应拦截器统一解析 Result{code,message,data}：code===200 取 data；否则以 message 抛错。
 */

import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

export const TOKEN_KEY = 'bluplanet_token';

const instance = axios.create({
  // 统一走 vite proxy '/api'（见 vite.config.ts server.proxy['/api'] -> gateway:8080）
  baseURL: '/api',
  timeout: 15000,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  // 安全（2026-10-20）：不再注入客户端可控的 X-User-Id 头（可被伪造冒充任意用户）。
  // 身份一律由网关从 JWT 解析后注入，后端只信任网关派生头。
  return config;
});

instance.interceptors.response.use(
  (response) => {
    const body = response.data;
    // 后端统一包装为 Result{code,message,data}
    if (body && typeof body.code === 'number') {
      if (body.code !== 200) {
        // 401：登录态失效（如 blu-ai /ai/chat 匿名闸门返回「登录已过期」）。
        // 清 token 并回登录页，避免把过期伪装成业务错误把用户留在原页（SPA 不会自动跳）。
        if (body.code === 401) {
          localStorage.removeItem(TOKEN_KEY);
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(new Error(body.message || '请求失败'));
      }
      // 直接返回 data 负载，调用方无需再 .data
      return body.data;
    }
    return body;
  },
  (error) => {
    const status = error?.response?.status;
    // 超时（axios code=ECONNABORTED / 'timeout of xxxms exceeded'）：
    // AI 对话等慢接口已单独放宽 timeout，其余接口超时多为服务端无响应。
    // 统一转中文文案，避免向终端用户暴露 axios 英文原文（如 'timeout of 15000ms exceeded'）。
    const isTimeout =
      error?.code === 'ECONNABORTED' ||
      (typeof error?.message === 'string' && error.message.toLowerCase().includes('timeout'));
    const message = isTimeout
      ? '服务响应超时，请稍后重试'
      : error?.response?.data?.message || error?.message || '网络错误，请稍后重试';
    if (status === 401) {
      localStorage.removeItem(TOKEN_KEY);
    }
    // 413：nginx 拦截超大上传（默认 1MB，测试机已放宽至 30MB）。nginx 返回的是 HTML 错误页，
    // error.response.data 没有 .message，兜底会变成 axios 英文 "Request failed with status code 413"，
    // 故在此特判为中文友好提示，避免向终端用户暴露原始错误。
    if (status === 413) {
      const rejected = new Error('文件过大，请压缩后上传（当前上限 30MB）') as Error & { status?: number };
      rejected.status = status;
      return Promise.reject(rejected);
    }
    // 将 HTTP 状态码挂在错误对象上，便于调用方（如履约节点 403）做精准文案分支
    const rejected = new Error(message) as Error & { status?: number };
    rejected.status = status;
    return Promise.reject(rejected);
  }
);

/** 泛型请求：自动解析 Result<T>.data 为 T */
export async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  return instance(config) as unknown as Promise<T>;
}

export default request;
